import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { IoChevronDownSharp } from "react-icons/io5";
import { createColumnHelper } from "@tanstack/react-table";

import {
  EMPTY_STRING,
  PAYLOAD_KEYS,
  USER_ROLE_MAPPING,
  SUBSCRIPTION_PLAN_MAPPING,
  SUBSCRIPTION_PLAN_TYPE_MAPPING,
  roleSelectOptions,
  planSelectOptions,
  planTypeSelectOptions,
  paymentHistoryConstants,
} from "../../../constants";
import {
  getSortBy,
  getPageNumber,
  buildFilterQuery,
  customStringSorting,
} from "../../../shared/utility";
import ReactTable from "../../../shared/components/tables/ReactTable";
import SearchField from "../../../shared/components/search/SearchField";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { paymentHistoryActions } from "../../../redux/actions";

const { GET_PAYMENT_HISTORY } = paymentHistoryConstants;
const { FILTER_QUERY, PAGE_SIZE, PAGE_NUMBER, SEARCH } = PAYLOAD_KEYS;

const PaymentHistory = (props) => {
  const {
    pagination,
    paymentHistory,
    spinnerActions,
    updatePagination,
    getPaymentHistory,
  } = props;

  const [sorting, setSorting] = useState({ id: "id", desc: true });

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("id", {
      header: <div className="column-head">Payment ID</div>,
      cell: ({ row: { original } }) => <div>{original.paymentId}</div>,
      size: 130,
      enableSorting: true,
    }),
    columnHelper.accessor("userName", {
      header: <div className="column-head">Name</div>,
      cell: ({ row: { original } }) => (
        <div title={original.userName} className="line-clamp">
          {original.userName}
        </div>
      ),
      size: 150,
      enableSorting: true,
      sortingFn: customStringSorting,
    }),
    columnHelper.accessor("userEmail", {
      header: <div className="column-head">Email</div>,
      cell: ({ row: { original } }) => (
        <div title={original.userEmail} className="line-clamp">
          {original.userEmail}
        </div>
      ),
      size: 150,
      enableSorting: true,
      sortingFn: customStringSorting,
    }),
    columnHelper.accessor("roleId", {
      header: <div className="column-head">Role</div>,
      cell: ({ row: { original } }) => (
        <div>{USER_ROLE_MAPPING[original.roleId]}</div>
      ),
      size: 100,
      enableSorting: true,
    }),
    columnHelper.accessor("planName", {
      header: <div className="column-head">Plan</div>,
      cell: ({ row: { original } }) => (
        <div>{SUBSCRIPTION_PLAN_MAPPING[original.plan]}</div>
      ),
      size: 110,
      enableSorting: true,
    }),
    columnHelper.accessor("planTypeName", {
      header: <div className="column-head">Type</div>,
      cell: ({ row: { original } }) => (
        <div>{SUBSCRIPTION_PLAN_TYPE_MAPPING[original.planType]}</div>
      ),
      size: 100,
      enableSorting: true,
    }),
    columnHelper.accessor("cardLastFourDigits", {
      header: <div className="column-head">Card Number</div>,
      cell: ({ row: { original } }) => (
        <div>xxxx xxxx xxxx {original.cardLastFourDigits}</div>
      ),
      size: 155,
      enableSorting: true,
    }),
    columnHelper.accessor("cardType", {
      header: <div className="column-head">Card Type</div>,
      cell: ({ row: { original } }) => (
        <div className="text-capitalize">{original.cardType}</div>
      ),
      size: 120,
      enableSorting: true,
      sortingFn: customStringSorting,
    }),
    columnHelper.accessor("price", {
      header: <div className="column-head">Amount</div>,
      cell: ({ row: { original } }) => (
        <div className="text-break">{`${original.priceCurrencyCode} ${original.amount}`}</div>
      ),
      size: 130,
      enableSorting: true,
    }),
  ];

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  const handleChangeRoleFilter = (value) => {
    updatePagination({
      ...pagination,
      role: value,
      plan: EMPTY_STRING,
      planType: EMPTY_STRING,
    });
  };

  const handleChangePlanFilter = (value) => {
    updatePagination({ ...pagination, plan: value });
  };

  const handleChangePlanTypeFilter = (value) => {
    updatePagination({ ...pagination, planType: value });
  };

  const resetFilter = () => {
    updatePagination({
      ...pagination,
      search: EMPTY_STRING,
      role: EMPTY_STRING,
      plan: EMPTY_STRING,
      planType: EMPTY_STRING,
    });
  };

  const handleUpdatePagination = (data, type) => {
    const payload = {
      ...pagination,
      [SEARCH]: type === SEARCH ? data : pagination.search,
      [PAGE_SIZE]: type === PAGE_SIZE ? data : pagination.pageSize,
      [PAGE_NUMBER]: getPageNumber(type, data, pagination),
    };

    updatePagination(payload);
  };

  const getFilterQuery = (data, roleFilter, planFilter, planTypeFilter) => {
    const searchFields = ["paymentId", "userName", "userEmail", "amount"];

    // Build the initial filter query from the data
    let filter = buildFilterQuery(data, searchFields);

    // Dynamically append the filters based on their presence
    if (roleFilter !== EMPTY_STRING) {
      filter =
        filter !== EMPTY_STRING ? `(${filter}) && (${roleFilter})` : roleFilter;
    }

    if (planFilter !== EMPTY_STRING) {
      filter =
        filter !== EMPTY_STRING ? `(${filter}) && (${planFilter})` : planFilter;
    }

    if (planTypeFilter !== EMPTY_STRING) {
      filter =
        filter !== EMPTY_STRING
          ? `(${filter}) && (${planTypeFilter})`
          : planTypeFilter;
    }

    return filter;
  };

  const getFilteredPlanOptions = () => {
    return planSelectOptions.filter(
      (option) =>
        !(
          pagination.role.includes(roleSelectOptions[0].value) &&
          option.id === planSelectOptions[0].id
        )
    );
  };

  useEffect(() => {
    return () => {
      resetFilter();
    };
  }, []);

  useEffect(() => {
    const updatedPayload = {
      ...pagination,
      [FILTER_QUERY]: getFilterQuery(
        pagination.search,
        pagination.role,
        pagination.plan,
        pagination.planType
      ),
      orderBy: getSortBy(sorting),
    };

    getPaymentHistory(updatedPayload);
    // eslint-disable-next-line
  }, [
    pagination.search,
    pagination.pageSize,
    pagination.pageNumber,
    pagination.role,
    pagination.plan,
    pagination.planType,
    sorting,
  ]);

  return (
    <MainContentWrapper>
      {spinner(GET_PAYMENT_HISTORY) > -1 && (
        <MainSpinner className="admin-spinner" />
      )}
      <div className="admin-page-heading">Payment History</div>

      <div
        className="
        d-flex 
        flex-wrap 
        flex-column 
        flex-lg-row 
        column-gap-4 
        align-items-lg-center
        "
      >
        <SearchField
          value={pagination.search}
          handleSearch={(val) => handleUpdatePagination(val, SEARCH)}
          className="
          w-auto 
          payment-history-search-field
          "
        />
        <div
          className="
          d-flex 
          flex-wrap 
          column-gap-4 
          align-items-center
          "
        >
          <div className="position-relative select-field">
            <select
              className="mt-4 w-auto"
              value={pagination.role}
              onChange={(e) => handleChangeRoleFilter(e.target.value)}
            >
              <option value={EMPTY_STRING}>Role</option>
              {roleSelectOptions.map((option) => (
                <option key={option.id} value={`roleId=${option.value}`}>
                  {option.label}
                </option>
              ))}
            </select>
            <IoChevronDownSharp className="mt-4" />
          </div>
          {!pagination.role.includes(roleSelectOptions[1].value) && (
            <div className="position-relative select-field">
              <select
                className="mt-4 w-auto"
                value={pagination.plan}
                onChange={(e) => handleChangePlanFilter(e.target.value)}
              >
                <option value={EMPTY_STRING}>Plan</option>
                {getFilteredPlanOptions().map((option) => (
                  <option key={option.id} value={`plan=${option.value}`}>
                    {option.label}
                  </option>
                ))}
              </select>
              <IoChevronDownSharp className="mt-4" />
            </div>
          )}
          <div className="position-relative select-field">
            <select
              className="mt-4 w-auto"
              value={pagination.planType}
              onChange={(e) => handleChangePlanTypeFilter(e.target.value)}
            >
              <option value={EMPTY_STRING}>Type</option>
              {planTypeSelectOptions.map((option) => (
                <option key={option.id} value={`planType=${option.value}`}>
                  {option.label}
                </option>
              ))}
            </select>
            <IoChevronDownSharp className="mt-4" />
          </div>
          <div className="mt-5 reset-filter" onClick={() => resetFilter()}>
            Reset Filter
          </div>
        </div>
      </div>
      <ReactTable
        columns={columns}
        data={[...paymentHistory]}
        paginationData={{
          currentPage: pagination.pageNumber,
          totalItems: pagination?.total ?? 0,
          pageSize: pagination.pageSize,
          onPageChange: (page) => handleUpdatePagination(page, PAGE_NUMBER),
          onPageSizeChange: (pageSize) =>
            handleUpdatePagination(pageSize, PAGE_SIZE),
        }}
        sortedBy={sorting}
        onColumnSort={(sortBy) => setSorting(sortBy)}
      />
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  paymentHistory: state.paymentHistoryReducer.paymentHistory,
  pagination: state.paymentHistoryReducer.pagination,
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  getPaymentHistory: (data) =>
    dispatch(paymentHistoryActions.getPaymentHistory(data)),
  updatePagination: (data) =>
    dispatch(paymentHistoryActions.updatePagination(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentHistory);
